const CAP8 = {
  id: 8,
  slug: "cap8-importancia-consenso",
  title: "Capítulo 8 — ¿Por qué es importante el consenso?",
  sections: [
    {
      heading: "El problema que resuelve blockchain",
      text: [
        "Imagina que miles de personas, desde distintas partes del mundo, deben ponerse de acuerdo sobre qué información es verdadera —pero algunas de esas personas pueden ser mentirosas, malintencionadas o incluso atacantes.",
        "Ese es el desafío que resuelve el consenso en blockchain.",
        "En informática, esto se conoce como el **Problema de los Generales Bizantinos**, un dilema clásico que muestra lo difícil que es llegar a un acuerdo cuando no todos dicen la verdad."
      ]
    },
    {
      heading: "¿Qué es el Problema de los Generales Bizantinos?",
      text: [
        "Imagina un grupo de generales rodeando un castillo. Todos deben coordinar un ataque, pero algunos podrían enviar mensajes falsos, confundiendo al resto.",
        "Si no hay certeza de que los mensajes sean auténticos, nadie sabe si debe atacar, esperar, o retirarse. El resultado podría ser un fracaso total.",
        "En blockchain pasa lo mismo: **muchos nodos deben ponerse de acuerdo sobre si una transacción es válida**, aunque algunos puedan intentar engañar al sistema."
      ],
      bluepoints: [
        "📌 No todos los participantes son confiables.",
        "📌 Los mensajes pueden ser falsificados.",
        "📌 Pero aun así, todos deben llegar a un acuerdo."
      ]
    },
    {
      heading: "¿Cómo lo resuelve blockchain?",
      text: [
        "Blockchain resolvió este problema usando el concepto de **Tolerancia a Fallas Bizantinas (BFT)**.",
        "Eso significa que la blockchain puede seguir funcionando correctamente, **aunque algunos participantes fallen, mientan o intenten atacar**.",
        "Los algoritmos de consenso (PoW, PoS, dPoS, etc.) permiten que los nodos lleguen a un acuerdo **de manera segura, verificable y descentralizada**."
      ],
      bluepoints: [
        "🔐 El sistema no confía en personas, sino en matemáticas.",
        "⚙️ Las reglas son iguales para todos.",
        "🛡️ Incluso si algunos participan con malas intenciones, la red sigue funcionando."
      ]
    },
    {
      heading: "¿Qué funciones cumple el consenso?",
      text: [
        "El consenso no solo permite que todos estén de acuerdo, sino que también protege la blockchain de errores, fraudes y manipulaciones.",
        "Aquí están sus funciones principales:"
      ],
      subsections: [
        {
          heading: " Seguridad",
          text: [
            "El consenso hace que sea mucho más difícil atacar la red que seguir las reglas. Para alterar la blockchain, un atacante necesitaría convencer o controlar a la mayoría de los nodos —algo prácticamente imposible en redes grandes como Bitcoin o Ethereum."
          ],
          list: [
            "Evita que los atacantes puedan modificar bloques.",
            "Protege la red contra manipulaciones y fraudes.",
            "Hace que atacar sea más caro que obedecer las reglas."
          ]
        },
        {
          heading: "Prevención de información falsa o spam",
          text: [
            "Antes de que una transacción se registre en la blockchain, debe ser verificada por los nodos. Esto bloquea intentos de incluir datos falsos, duplicados o manipulados."
          ],
          list: [
            "Solo se aceptan transacciones legítimas.",
            "Bloques alterados o fraudulentos son rechazados.",
            "Evita ataques de spam o transacciones falsas."
          ]
        },
        {
          heading: "Evitar el doble gasto",
          text: [
            "El doble gasto ocurre cuando alguien intenta enviar la misma criptomoneda dos veces, como si copiara billetes digitales.",
            "Gracias al consenso y las firmas criptográficas, esto queda bloqueado automáticamente."
          ],
          list: [
            "Cada transacción es única y verificable.",
            "Una vez confirmada, no se puede revertir.",
            "Nadie puede 'duplicar' sus criptomonedas."
          ]
        },
        {
          heading: " Mantener la descentralización",
          text: [
            "Sin consenso, alguien tendría que decidir qué transacciones son válidas (como un banco o servidor central).",
            "Pero con consenso, **nadie manda solo**, sino que **la verdad se decide entre miles de nodos independientes**."
          ],
          bluepoints: [
            "🔹 No hay un dueño del sistema.",
            "🔹 Nadie puede alterar la información por sí solo.",
            "🔹 La red permanece abierta, colaborativa y resistente a censura."
          ]
        }
      ]
    },
    {
      heading: "Conclusión",
      text: [
        "El consenso es la magia que permite que una blockchain funcione sin bancos, sin servidores centrales y sin un jefe que decida qué es válido y qué no.",
        "Gracias al consenso, la blockchain puede ser **segura, transparente, incorruptible y descentralizada**, incluso cuando algunos participantes intentan engañar al sistema.",
        "Por eso se considera uno de los pilares fundamentales del mundo cripto."
      ],
      bluepoints: [
        "🧠 Sin consenso, no hay blockchain.",
        "🛡️ Sin seguridad, no hay confianza.",
        "🌐 Sin descentralización, no hay libertad."
      ]
    }
  ]
};

export default CAP8;
